// Plan de cuentas (NIIF). Contabilidad → Catálogos → Plan de Cuentas.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Switch, fieldCls } from '@/features/inventario/shared';
import { useTranslation } from 'react-i18next';
import { Cuenta, cargarPlan } from './shared';

const TIPOS = ['activo', 'pasivo', 'patrimonio', 'ingreso', 'costo', 'gasto'];
const natDe = (tipo: string) => (['activo', 'costo', 'gasto'].includes(tipo) ? 'deudora' : 'acreedora');
const esResultado = (tipo: string) => ['ingreso', 'costo', 'gasto'].includes(tipo);
const CATS_NIIF18 = [
  { v: 'operacion', label: 'Operación' }, { v: 'inversion', label: 'Inversión' },
  { v: 'financiacion', label: 'Financiación' }, { v: 'impuestos', label: 'Impuestos a las ganancias' },
  { v: 'discontinuadas', label: 'Operaciones discontinuadas' },
];
const esGasto = (tipo: string) => ['costo', 'gasto'].includes(tipo);
const NATS_GASTO = [
  { v: 'depreciacion', label: 'Depreciación' }, { v: 'amortizacion', label: 'Amortización' },
  { v: 'beneficios_empleados', label: 'Beneficios a empleados' }, { v: 'deterioro', label: 'Pérdidas por deterioro' },
  { v: 'inventarios', label: 'Disminución valor de inventarios' }, { v: 'otros', label: 'Otros' },
];
const base = '/rest/contabilidad.cuentas';
const vacio = { parent_id: '', codigo: '', nombre: '', tipo: 'activo', es_movimiento: true, es_activo: true, categoria_niif18: '', naturaleza_gasto: '' };

export function PlanCuentas() {
  const { t } = useTranslation('contabilidad');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cuentas = [], isFetching } = useQuery<Cuenta[]>({ queryKey: ['cont-plan'], queryFn: cargarPlan });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const abrir = (c?: Cuenta) => {
    if (c) { setEditId(c.id); setForm({ parent_id: c.parent_id ?? '', codigo: c.codigo, nombre: c.nombre, tipo: c.tipo, es_movimiento: c.es_movimiento, es_activo: c.es_activo, categoria_niif18: c.categoria_niif18 ?? '', naturaleza_gasto: c.naturaleza_gasto ?? '' }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: async () => {
      const body = { parent_id: form.parent_id || null, codigo: form.codigo, nombre: form.nombre, tipo: form.tipo, naturaleza: natDe(form.tipo), es_movimiento: form.es_movimiento, es_activo: form.es_activo, categoria_niif18: esResultado(form.tipo) ? (form.categoria_niif18 || null) : null, naturaleza_gasto: esGasto(form.tipo) ? (form.naturaleza_gasto || null) : null };
      return editId ? apiClient.patch(`${base}?id=eq.${editId}`, body) : apiClient.post(base, body);
    },
    onSuccess: () => { toast.success(t('plan_cuentas.cuenta_guardada')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['cont-plan'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => apiClient.delete(`${base}?id=eq.${id}`),
    onSuccess: () => { toast.success(t('plan_cuentas.cuenta_eliminada')); void qc.invalidateQueries({ queryKey: ['cont-plan'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const sembrar = useMutation({
    mutationFn: () => apiClient.post('/rpc/cont_sembrar_plan', {}, { headers: { 'Content-Profile': 'contabilidad' } }),
    onSuccess: () => { toast.success(t('plan_cuentas.plan_cargado')); void qc.invalidateQueries({ queryKey: ['cont-plan'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const depth = (cod: string) => cod.split('.').length - 1;
  const padres = useMemo(() => cuentas.filter((c) => !c.es_movimiento), [cuentas]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      {abierto ? (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}><ArrowLeft className="w-4 h-4 mr-1" /> {t('comun.volver')}</Button>
          <h1 className="text-xl font-semibold">{editId ? t('plan_cuentas.editar_cuenta') : t('plan_cuentas.nueva_cuenta')}</h1>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">{t('plan_cuentas.titulo')}</h1>
          <div className="flex gap-2">
            {cuentas.length === 0 && <Button variant="outline" isLoading={sembrar.isPending} onClick={() => sembrar.mutate()}>{t('plan_cuentas.cargar_plan_base')}</Button>}
            <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> {t('plan_cuentas.nueva_cuenta')}</Button>
          </div>
        </div>
      )}
      {!abierto && (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('plan_cuentas.cuentas_n', { n: cuentas.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('comun.codigo')}</th><th className="py-2 pr-3">{t('comun.cuenta')}</th><th className="py-2 pr-3">{t('plan_cuentas.col_tipo')}</th>
              <th className="py-2 pr-3">{t('plan_cuentas.col_mov')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {cuentas.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('plan_cuentas.sin_cuentas')}</td></tr>}
              {cuentas.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-1.5 pr-3 font-mono text-xs">{c.codigo}</td>
                  <td className="py-1.5 pr-3"><span style={{ paddingLeft: depth(c.codigo) * 16 }} className={c.es_movimiento ? '' : 'font-semibold'}>{c.nombre}</span></td>
                  <td className="py-1.5 pr-3 text-muted-foreground">{t(`tipos.${c.tipo}`, { defaultValue: c.tipo })}</td>
                  <td className="py-1.5 pr-3">{c.es_movimiento ? t('plan_cuentas.si') : '—'}</td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('comun.editar')} onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title={t('comun.eliminar')} onClick={async () => { if (await dlg.confirmar(t('plan_cuentas.confirmar_eliminar', { codigo: c.codigo, nombre: c.nombre }), { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      )}

      {abierto && (
        <Card>
          <CardContent className="pt-4">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('plan_cuentas.lbl_codigo')}><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="1.1.01.03" /></Campo>
              <Campo label={t('plan_cuentas.lbl_tipo')}>
                <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{TIPOS.map((tp) => <option key={tp} value={tp}>{t(`tipos.${tp}`)}</option>)}</select>
              </Campo>
            </div>
            <Campo label={t('plan_cuentas.lbl_nombre')}><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <Campo label={t('plan_cuentas.lbl_padre')}>
              <select className={fieldCls} value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
                <option value="">{t('plan_cuentas.raiz')}</option>
                {padres.filter((c) => c.id !== editId).map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
              </select>
            </Campo>
            {esResultado(form.tipo) && (
              <Campo label={t('plan_cuentas.lbl_cat18')}>
                <select className={fieldCls} value={form.categoria_niif18} onChange={(e) => setForm({ ...form, categoria_niif18: e.target.value })}>
                  <option value="">{t('plan_cuentas.cat18_vacio')}</option>
                  {CATS_NIIF18.map((x) => <option key={x.v} value={x.v}>{t(`cat18.${x.v}`)}</option>)}
                </select>
              </Campo>
            )}
            {esGasto(form.tipo) && (
              <Campo label={t('plan_cuentas.lbl_nat_gasto')}>
                <select className={fieldCls} value={form.naturaleza_gasto} onChange={(e) => setForm({ ...form, naturaleza_gasto: e.target.value })}>
                  <option value="">{t('plan_cuentas.nat_gasto_vacio')}</option>
                  {NATS_GASTO.map((x) => <option key={x.v} value={x.v}>{t(`nat_gasto.${x.v}`)}</option>)}
                </select>
              </Campo>
            )}
            <div className="flex items-center gap-6">
              <Switch checked={form.es_movimiento} onChange={(v) => setForm({ ...form, es_movimiento: v })} label={t('plan_cuentas.acepta_mov')} />
              <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label={t('plan_cuentas.activa')} />
            </div>
            <p className="text-xs text-muted-foreground">{t('plan_cuentas.naturaleza_auto', { nat: t(`naturaleza.${natDe(form.tipo)}`) })}</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun.cancelar')}</Button>
              <Button isLoading={guardar.isPending} disabled={!form.codigo.trim() || !form.nombre.trim()} onClick={() => guardar.mutate()}>{t('comun.guardar')}</Button>
            </div>
          </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
